import * as store from '@attain-apps/fe/web/main/lib/store'

import * as ecModels from '../../../models'
import type * as dataOperation from '../data-operation'

import type { Plugin } from './plugin'

export class Selectors<TTypeParams extends ecModels.TypeParams.Constraint> {
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getId
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getEntity
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getPosition

  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getNextId
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getNextEntity

  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getPreviousId
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getPreviousEntity

  // NOTE: intentionally not typed here so that type can be inferred
  public readonly isEditing
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly isCreating
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly isUpdating
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getEditedEntity
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getEditedEntityAndId
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getCreationEntity
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getUpdatedEntity

  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getLatestEntity
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getLatestEntityAndId

  /**
   * Indicates the desired current entity _might_ be loading. This is `true`
   * when the entity itself is being loaded or the current list is being loaded,
   * since the current list may contain this entity
   *
   * __NOTE:__ it is usually recommended that you only consider this when the
   * current entity is `undefined`. If the current entity is defined, you should
   * probably use/display it whether or not this is `true`
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly isLoading
  /**
   * Represents an error that _may_ have prevented the current entity from being
   * loaded. This can be an error from retrieving the entity itself or an error
   * from retrieving the current list, since the current list may contain this
   * entity
   *
   * __NOTE:__ it is usually recommended that you only consider this when the
   * current entity is `undefined`. If the current entity is defined, you should
   * probably use/display it whether or not this has an error
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly getError

  constructor(plugin: Plugin<TTypeParams>) {
    function createTargetBasedSelectorMethod<TSelection>(
      cb: (target: ecModels.Target) => store.selectors.Selector<TSelection>,
    ) {
      return plugin.feature.createSelectorMethod(
        ({
          target = ecModels.Target.DEFAULT,
        }: { readonly target?: ecModels.Target } = {}) => cb(target),
      )
    }

    this.getId = createTargetBasedSelectorMethod(
      target => plugin.storage.selectors.getFromIdLookup(target).asSelector,
    )

    const createEntitySelectorMethodFromIdSelectorMethod = (
      idSelectorMethod: this['getId'],
    ) =>
      createTargetBasedSelectorMethod(
        target =>
          idSelectorMethod
            .call(this, { target })
            .project(
              plugin.collection.plugins.cachePlugin.feature.selectors.lookup
                .asSelector,
              (id, lookup) => (id === undefined ? undefined : lookup[id]),
            ).asSelector,
      )
    this.getEntity = createEntitySelectorMethodFromIdSelectorMethod(this.getId)

    this.getPosition = createTargetBasedSelectorMethod(
      target =>
        this.getId({ target }).project(
          plugin.collection.plugins.currentListPlugin.feature.selectors.ids
            .asSelector,
          (id, ids) => {
            if (id === undefined) {
              return undefined
            }
            const position = ids.indexOf(id)
            return position === -1 ? undefined : position
          },
        ).asSelector,
    )
    const createRelativePositionSelectorMethod = (offset: 1 | -1) =>
      createTargetBasedSelectorMethod(
        target =>
          this.getPosition({ target }).project(
            plugin.collection.plugins.currentListPlugin.feature.selectors.ids
              .asSelector,
            (position, ids) =>
              position === undefined ? undefined : ids[position + offset],
          ).asSelector,
      )
    this.getNextId = createRelativePositionSelectorMethod(1)
    this.getPreviousId = createRelativePositionSelectorMethod(-1)
    this.getNextEntity = createEntitySelectorMethodFromIdSelectorMethod(
      this.getNextId,
    )
    this.getPreviousEntity = createEntitySelectorMethodFromIdSelectorMethod(
      this.getPreviousId,
    )

    const createEditSelectorMethodFrom = <
      TLookup extends
        | typeof plugin.collection.plugins.editingPlugin.feature.selectors.creationsLookup
        | typeof plugin.collection.plugins.editingPlugin.feature.selectors.updatesLookup,
    >(
      boundLookupSelector: TLookup,
    ) =>
      createTargetBasedSelectorMethod(target =>
        store.selectors.Selector.create(
          this.getId({ target }).asSelector,
          boundLookupSelector.asSelector,
          (id, lookup) =>
            id === undefined
              ? undefined
              : // NOTE: unfortunately the return type casting is needed to get
                // the return type to be correct solely because TypeScript is
                // not inferring types correctly for `lookup`
                ((lookup as Generalize.Lookup.From<typeof lookup>)[
                  id
                ] as IndexUnion<ReturnType<TLookup['asSelector']>>),
        ),
      )
    this.getCreationEntity = createEditSelectorMethodFrom(
      plugin.collection.plugins.editingPlugin.feature.selectors.creationsLookup,
    )
    this.getUpdatedEntity = createEditSelectorMethodFrom(
      plugin.collection.plugins.editingPlugin.feature.selectors.updatesLookup,
    )
    this.getEditedEntity = createTargetBasedSelectorMethod(target =>
      store.selectors.Selector.create(
        this.getCreationEntity({ target }).asSelector,
        this.getUpdatedEntity({ target }).asSelector,
        (creation, update) => creation ?? update,
      ),
    )
    this.getEditedEntityAndId = createTargetBasedSelectorMethod(target =>
      store.selectors.Selector.create(
        this.getId({ target }).asSelector,
        this.getEditedEntity({ target }).asSelector,
        (id, entity) => ({ entity, id }) as const,
      ),
    )

    this.getLatestEntity = createTargetBasedSelectorMethod(target =>
      store.selectors.Selector.create(
        this.getEditedEntity({ target }).asSelector,
        this.getEntity({ target }).asSelector,
        (editedEntity, entity) => editedEntity ?? entity,
      ),
    )
    this.getLatestEntityAndId = createTargetBasedSelectorMethod(target =>
      store.selectors.Selector.create(
        this.getId({ target }).asSelector,
        this.getLatestEntity({ target }).asSelector,
        (id, entity) => ({ entity, id }) as const,
      ),
    )

    this.isCreating = createTargetBasedSelectorMethod(target =>
      store.selectors.Selector.create(
        this.getCreationEntity({ target }).asSelector,
        creation => !!creation,
      ),
    )
    this.isUpdating = createTargetBasedSelectorMethod(target =>
      store.selectors.Selector.create(
        this.getUpdatedEntity({ target }).asSelector,
        update => !!update,
      ),
    )
    this.isEditing = createTargetBasedSelectorMethod(target =>
      store.selectors.Selector.create(
        this.isCreating({ target }).asSelector,
        this.isUpdating({ target }).asSelector,
        (isCreating, isUpdating) => isCreating || isUpdating,
      ),
    )

    const createStatusSelectorMethodFrom = <T, TExtraArg>(
      additionalSelector: store.selectors.BoundSelector<TExtraArg>,
      cb: (
        status: dataOperation.models.StatusLookup.Entry | undefined,
        extra: TExtraArg,
      ) => T,
    ) =>
      createTargetBasedSelectorMethod(target =>
        store.selectors.Selector.create(
          this.getId({ target }).asSelector,
          plugin.collection.plugins.dataOperationPlugin.feature.selectors
            .statusLookup.asSelector,
          additionalSelector.asSelector,
          (id, lookup, extra) =>
            cb(id === undefined ? undefined : lookup[id], extra),
        ),
      )
    this.isLoading = createStatusSelectorMethodFrom(
      plugin.collection.plugins.currentListPlugin.feature.selectors.isLoading,
      (status, listIsLoading) => !!status?.isLoading || listIsLoading,
    )
    this.getError = createStatusSelectorMethodFrom(
      plugin.collection.plugins.currentListPlugin.feature.selectors.error,
      (status, listError) => status?.error ?? listError,
    )
  }
}
